import React, { useState } from "react";
import { LGrid, LButton } from "@loffa/gpp-modules";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight } from "@fortawesome/free-solid-svg-icons";
import styles from "./GabSyncManagement.module.css";
import { GabSyncRunDetail, GabSyncRunSummary } from "./types";

interface Props {
  runs: GabSyncRunSummary[];
  loading: boolean;
  onRefresh: () => void;
  loadDetail: (runId: string) => Promise<GabSyncRunDetail>;
}

interface CellProps {
  dataItem: GabSyncRunSummary;
}

function statusClass(status: string): string {
  switch (status) {
    case "Applied":
      return styles.statusNew;
    case "Failed":
      return styles.statusConflict;
    case "AwaitingReview":
    case "Draft":
      return styles.statusChanged;
    default:
      return styles.statusUnchanged;
  }
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleString();
}

function RunHistory({ runs, loading, onRefresh, loadDetail }: Props) {
  const [detail, setDetail] = useState<GabSyncRunDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  const openDetail = (runId: string) => {
    setDetailError(null);
    loadDetail(runId)
      .then((d) => setDetail(d))
      .catch((e) => setDetailError(e.message));
  };

  const columns = [
    {
      field: "status",
      title: "Status",
      width: 130,
      cell: (props: CellProps) => (
        <td>
          <span className={`${styles.statusBadge} ${statusClass(props.dataItem.status)}`}>
            {props.dataItem.status}
          </span>
        </td>
      ),
    },
    {
      field: "targetLabel",
      title: "Target",
      width: 140,
      cell: (props: CellProps) => <td>{props.dataItem.targetLabel}</td>,
    },
    {
      field: "createdOn",
      title: "Compared",
      width: 180,
      cell: (props: CellProps) => <td>{formatDate(props.dataItem.createdOn)}</td>,
    },
    {
      field: "completedOn",
      title: "Applied",
      width: 180,
      cell: (props: CellProps) => <td>{formatDate(props.dataItem.completedOn)}</td>,
    },
    {
      field: "appliedInserted",
      title: "Ins / Upd / Skip",
      width: 140,
      filterable: false,
      cell: (props: CellProps) => (
        <td>
          {props.dataItem.appliedInserted} / {props.dataItem.appliedUpdated} /{" "}
          {props.dataItem.appliedSkipped}
        </td>
      ),
    },
    {
      field: "completedBy",
      title: "By",
      width: 140,
      cell: (props: CellProps) => (
        <td>{props.dataItem.completedBy || props.dataItem.createdBy}</td>
      ),
    },
    {
      field: "detail",
      title: "",
      width: 90,
      filterable: false,
      cell: (props: CellProps) => (
        <td>
          <button
            type="button"
            className={styles.detailButton}
            onClick={() => openDetail(props.dataItem.id)}
          >
            View
          </button>
        </td>
      ),
    },
  ];

  return (
    <div className={styles.historyContainer}>
      <div className={styles.panelIntro}>
        <p className={styles.introText}>Every compare and apply, most recent first.</p>
        <LButton look="flat" onClick={onRefresh} disabled={loading}>
          <FontAwesomeIcon icon={faRotateRight} /> Refresh
        </LButton>
      </div>

      <LGrid
        data={runs}
        columns={columns}
        loading={loading}
        sortable
        filterable
        resizable
        pageable
        style={{ maxHeight: "420px" }}
        className={styles.grid}
      />

      {detailError && <div className={styles.mutedText}>{detailError}</div>}

      {detail && (
        <div className={styles.detailPanel}>
          <div className={styles.detailPanelTitle}>
            Run {detail.summary.id} — {detail.summary.status}
            {detail.summary.errorMessage && (
              <span className={styles.statusConflict}> · {detail.summary.errorMessage}</span>
            )}
          </div>

          {detail.snapshotTables.length > 0 && (
            <div className={styles.snapshotList}>
              <span className={styles.summaryLabel}>Rollback snapshots:</span>{" "}
              {detail.snapshotTables.map((t) => (
                <code key={t} className={styles.snapshotTag}>
                  {t}
                </code>
              ))}
            </div>
          )}

          {detail.outcomes.length > 0 ? (
            <table className={styles.detailTable}>
              <thead>
                <tr>
                  <th>Table</th>
                  <th>Key</th>
                  <th>Outcome</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {detail.outcomes.map((o, i) => (
                  <tr key={`${o.tableCode}-${o.keyValue}-${i}`}>
                    <td className={styles.detailColName}>{o.tableCode}</td>
                    <td>{o.keyValue}</td>
                    <td>{o.outcome}</td>
                    <td>
                      {o.isCustom ? (
                        <span className={styles.statusChanged}>custom edit</span>
                      ) : (
                        <span className={styles.mutedText}>source</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={styles.mutedText}>No apply outcomes recorded for this run.</div>
          )}
        </div>
      )}
    </div>
  );
}

export { RunHistory };
